import { AxiosResponse } from 'axios';

import apiClient from '../client';
import i18n from 'i18n';
import { PageResultDto } from 'interfaces/page';
import { CreateEmployeeDto, EmployeeDto, EmployeeThinDto, UpdateEmployeeDto } from 'interfaces/employee';
import { ApiResponseDto } from 'interfaces/apiResponse';
import { AvailabilityDto, AvailabilityQueryDto } from 'interfaces/employee_availability';

interface EmployeesService {
  searchEmployees(queryParams?: any): Promise<PageResultDto<EmployeeDto>>;
  getEmployee(id: string): Promise<EmployeeDto>;
  createEmployee(data: CreateEmployeeDto): Promise<AxiosResponse<EmployeeDto>>;
  editEmployee(id: string, data: UpdateEmployeeDto): Promise<AxiosResponse<EmployeeDto>>;
  getAvailabilities(queryParams: AvailabilityQueryDto): Promise<AvailabilityDto>;
  getAvailableEmployees(params?: { availableOnDate: string; branchId?: number | null }): Promise<EmployeeThinDto[]>;
}

const endpoints = {
  searchEmployees: '/employees',
  getEmployee: (id: string) => `/employees/${id}`,
  createEmployee: '/employees',
  editEmployee: (id: string) => `/employees/${id}`,
  getAvailabilities: '/employees/availability',
  getAvailableEmployees: '/employees/available',
};

export const employeesService: EmployeesService = {
  searchEmployees: async (queryParams?: any): Promise<PageResultDto<EmployeeDto>> => {
    const response: AxiosResponse<ApiResponseDto<PageResultDto<EmployeeDto>>> = await apiClient.get(endpoints.searchEmployees, {
      params: { ...queryParams },
      paramsSerializer: { indexes: null },
    });

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  getEmployee: async (id: string): Promise<EmployeeDto> => {
    const response: AxiosResponse<ApiResponseDto<EmployeeDto>> = await apiClient.get(endpoints.getEmployee(id));

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  createEmployee: async (data: CreateEmployeeDto): Promise<AxiosResponse<EmployeeDto>> => {
    try {
      return await apiClient.post(endpoints.createEmployee, data);
    } catch (error: any) {
      // Handle structured API errors
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        const firstError = apiErrors[0];

        if (firstError.code === 'ACTIVE_EMPLOYEE_LIMIT_REACHED_FOR_TIER' && firstError.details) {
          const { tierCode, employeeLimit } = firstError.details;
          throw new Error(i18n.t('apiErrors.activeEmployeeLimitReachedForTier', { tierCode, employeeLimit }));
        }

        // Fallback to original error message for other structured errors
        throw new Error(firstError.message);
      }

      // Re-throw original error if not a structured API error
      throw error;
    }
  },

  editEmployee: async (id: string, data: UpdateEmployeeDto): Promise<AxiosResponse<EmployeeDto>> => {
    return apiClient.put(endpoints.editEmployee(id), data);
  },

  getAvailabilities: async (params?: AvailabilityQueryDto): Promise<AvailabilityDto> => {
    const response: AxiosResponse<ApiResponseDto<AvailabilityDto>> = await apiClient.get(endpoints.getAvailabilities, { params });

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },

  getAvailableEmployees: async (params?: { availableOnDate: string; branchId?: number | null }): Promise<EmployeeThinDto[]> => {
    const response: AxiosResponse<ApiResponseDto<EmployeeThinDto[]>> = await apiClient.get(endpoints.getAvailableEmployees, { params });

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data!;
  },
};
